import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { colors, radii } from '../theme/tokens';
import Icon from './ui/Icon';

const InputContext = createContext(null);

export const InputProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [value, setValue] = useState('');
  const resolverRef = useRef(null);
  const inputRef = useRef(null);

  const showInput = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setValue(options.defaultValue || '');
      setDialog({ message, ...options });
    });
  }, []);

  useEffect(() => {
    if (dialog) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [dialog]);

  const handleConfirm = () => {
    if (dialog?.required && !value.trim()) return;
    const result = value.trim() || null;
    setDialog(null);
    setValue('');
    resolverRef.current?.(result);
  };

  const handleCancel = () => {
    setDialog(null);
    setValue('');
    resolverRef.current?.(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleConfirm();
    if (e.key === 'Escape') handleCancel();
  };

  return (
    <InputContext.Provider value={showInput}>
      {children}
      {dialog && (
        <div style={styles.overlay} onClick={handleCancel}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.iconWrap}>
              <span style={styles.iconCircle}>
                <Icon name={dialog.icon || 'edit'} size={28} color={colors.parGreenBright} />
              </span>
            </div>
            <h3 style={styles.title}>{dialog.title || 'Enter Value'}</h3>
            <p style={styles.message}>{dialog.message}</p>
            <input
              ref={inputRef}
              type={dialog.inputType || 'text'}
              placeholder={dialog.placeholder || ''}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              style={styles.input}
              min={dialog.min}
              max={dialog.max}
            />
            {dialog.hint && <p style={styles.hint}>{dialog.hint}</p>}
            <div style={styles.buttons}>
              <button onClick={handleCancel} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                style={styles.confirmBtn}
                disabled={dialog?.required && !value.trim()}
              >
                {dialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </InputContext.Provider>
  );
};

export const useInputDialog = () => {
  const ctx = useContext(InputContext);
  if (!ctx) throw new Error('useInputDialog must be used within InputProvider');
  return ctx;
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: colors.overlay,
    zIndex: 99998,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backdropFilter: 'blur(2px)'
  },
  modal: {
    background: colors.menuBg,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.lg,
    padding: '32px 28px 24px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    textAlign: 'center',
    color: colors.text,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)'
  },
  iconWrap: {
    marginBottom: '14px',
    display: 'flex',
    justifyContent: 'center'
  },
  iconCircle: {
    width: 52, height: 52, borderRadius: '50%', background: 'rgba(47,182,92,0.14)',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  title: {
    margin: '0 0 8px',
    fontSize: '18px',
    color: colors.text,
    fontWeight: '700'
  },
  message: {
    margin: '0 0 18px',
    fontSize: '15px',
    color: colors.textMuted,
    lineHeight: '1.5'
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    background: colors.inputBg,
    color: colors.text,
    border: `2px solid ${colors.parGreen}`,
    borderRadius: radii.md,
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '8px',
    textAlign: 'center',
    fontWeight: '600'
  },
  hint: {
    margin: '0 0 16px',
    fontSize: '12px',
    color: colors.textFaint
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginTop: '20px'
  },
  cancelBtn: {
    padding: '11px 24px',
    background: colors.glass,
    color: colors.text,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.pill,
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    flex: 1,
    maxWidth: '140px'
  },
  confirmBtn: {
    padding: '11px 24px',
    background: colors.parGreen,
    color: 'white',
    border: 'none',
    borderRadius: radii.pill,
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    flex: 1,
    maxWidth: '140px'
  }
};
